
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Car, CarRequest, TestDriveRequest, CarStatus } from '../types';
import { dataService, auth } from '../services/dataService';
import { excelService } from '../services/excelService';
import { signInWithEmailAndPassword, createUserWithEmailAndPassword, sendPasswordResetEmail, signOut, onAuthStateChanged, updatePassword } from 'firebase/auth';
import { 
  Trash2, Edit, Plus, User, Calendar, Car as CarIcon, LogOut, 
  Upload, X as CloseIcon, Image as ImageIcon, Mail, Phone, 
  Clock, LayoutDashboard, Lock, ShieldCheck, Briefcase, ChevronRight,
  Database, Sparkles, AlertTriangle, ArrowLeft, ArrowRight, Move,
  FileSpreadsheet, Download, Check, UploadCloud, Flame, UserPlus, Users, Key
} from 'lucide-react';
import { useForm } from 'react-hook-form';
import { initializeApp, getApp } from 'firebase/app';
import { getAuth } from 'firebase/auth';

type UserRole = 'staff' | 'admin' | 'sales_manager' | null;

// Utility to resize and compress images to stay under Firestore's 1MB limit
const compressImage = (file: File): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = (event) => {
      const img = new Image();
      img.src = event.target?.result as string;
      img.onload = () => {
        const canvas = document.createElement('canvas');
        // Reduced from 1024 to 720 to comfortably support 15+ images under 1MB document limit
        const MAX_WIDTH = 720;
        const MAX_HEIGHT = 720;
        let width = img.width;
        let height = img.height;

        if (width > height) {
          if (width > MAX_WIDTH) {
            height *= MAX_WIDTH / width;
            width = MAX_WIDTH;
          }
        } else {
          if (height > MAX_HEIGHT) {
            width *= MAX_HEIGHT / height;
            height = MAX_HEIGHT;
          }
        }

        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        ctx?.drawImage(img, 0, 0, width, height);
        
        // Convert to JPEG with 0.5 quality to optimize size while maintaining good clarity (~20-30KB)
        const compressedBase64 = canvas.toDataURL('image/jpeg', 0.5);
        resolve(compressedBase64);
      };
      img.onerror = (err) => reject(err);
    };
    reader.onerror = (err) => reject(err);
  });
};

export const Admin: React.FC = () => {
  const navigate = useNavigate();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [userRole, setUserRole] = useState<UserRole>(null);
  const [activeTab, setActiveTab] = useState<'inventory' | 'requests' | 'testdrives' | 'staff'>('inventory');
  
  // Data State
  const [cars, setCars] = useState<Car[]>([]);
  const [requests, setRequests] = useState<CarRequest[]>([]);
  const [testDrives, setTestDrives] = useState<TestDriveRequest[]>([]);

  // Staff and Profile State
  const [staffName, setStaffName] = useState('');
  const [staffMembers, setStaffMembers] = useState<any[]>([]);
  const [isLoadingStaff, setIsLoadingStaff] = useState(false);
  const [newStaffName, setNewStaffName] = useState('');
  const [newStaffEmail, setNewStaffEmail] = useState('');
  const [newStaffPassword, setNewStaffPassword] = useState('');
  const [newStaffRole, setNewStaffRole] = useState<'admin' | 'sales_manager' | 'staff'>('sales_manager');
  const [isAddingStaff, setIsAddingStaff] = useState(false);
  const [editingStaffId, setEditingStaffId] = useState<string | null>(null);
  const [staffError, setStaffError] = useState<string | null>(null);
  const [staffSuccess, setStaffSuccess] = useState<string | null>(null);

  // Change password state
  const [newPasswordValue, setNewPasswordValue] = useState('');
  const [isChangingPassword, setIsChangingPassword] = useState(false);
  const [passwordError, setPasswordError] = useState<string | null>(null);
  const [passwordSuccess, setPasswordSuccess] = useState<string | null>(null);
  
  // UI State
  const [isAddingCar, setIsAddingCar] = useState(false);
  const [editingCarId, setEditingCarId] = useState<string | null>(null);
  const [uploadedImages, setUploadedImages] = useState<string[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const [isLoadingData, setIsLoadingData] = useState(false);
  const [isSeeding, setIsSeeding] = useState(false);
  const [draggedIdx, setDraggedIdx] = useState<number | null>(null);
  const [underConstruction, setUnderConstruction] = useState(false);

  // Excel Import / Export State
  const [isExcelPanelOpen, setIsExcelPanelOpen] = useState(false);
  const [excelImportCars, setExcelImportCars] = useState<Omit<Car, 'id'>[] | null>(null);
  const [excelImportErrors, setExcelImportErrors] = useState<string[] | null>(null);
  const [isProcessingExcel, setIsProcessingExcel] = useState(false);
  const [excelFileName, setExcelFileName] = useState<string>('');

  const handleExcelUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setExcelFileName(file.name);
    setIsProcessingExcel(true);
    setExcelImportErrors(null);
    setExcelImportCars(null);
    
    try {
      const result = await excelService.parseExcelFile(file);
      if (result.success && result.data.length > 0) {
        setExcelImportCars(result.data);
      } else {
        setExcelImportErrors(result.errors.length > 0 ? result.errors : ['No valid vehicle data found in the spreadsheet.']);
      }
    } catch (err: any) {
      setExcelImportErrors([err.message || 'Error parsing file']);
    } finally {
      setIsProcessingExcel(false);
      e.target.value = ''; // Reset file input
    }
  };

  const handleApplyExcelImport = async (mode: 'append' | 'replace') => {
    if (!excelImportCars || excelImportCars.length === 0) return;
    
    if (mode === 'replace') {
      if (!confirm("Are you SURE you want to OVERWRITE the entire catalog with these spreadsheet listings? This will erase all existing vehicles!")) {
        return;
      }
    }
    
    setIsLoadingData(true);
    try {
      if (mode === 'replace') {
        const currentCars = await dataService.getCars();
        for (const car of currentCars) {
          await dataService.deleteCar(car.id);
        }
      }
      
      // Batch-add from Excel
      for (const car of excelImportCars) {
        await dataService.addCar(car);
      }
      
      alert(`Successfully loaded ${excelImportCars.length} vehicles to the inventory.`);
      setExcelImportCars(null);
      setExcelFileName('');
      setIsExcelPanelOpen(false);
      loadData();
    } catch (err) {
      alert("Failed storing vehicles to database.");
    } finally {
      setIsLoadingData(false);
    }
  };

  // Login State
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loginRole, setLoginRole] = useState<'staff' | 'admin'>('staff');
  const [isForgotPassword, setIsForgotPassword] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [authError, setAuthError] = useState<string | null>(null);
  const [authLoading, setAuthLoading] = useState(false);
  const [resetSuccess, setResetSuccess] = useState(false);

  const { register, handleSubmit, reset, formState: { errors } } = useForm<Partial<Car>>();

  // Auto resume session or set role on auth state change
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        setIsAuthenticated(true);
        try {
          const staffRecord = user.email ? await dataService.getStaffMemberByEmail(user.email) : undefined;
          if (staffRecord) {
            setUserRole(staffRecord.role as UserRole);
            setStaffName(staffRecord.name);
          } else if (user.email === 'nassoroa@gmail.com' || user.email?.includes('admin')) {
            setUserRole('admin');
            setStaffName('Owner');
          } else {
            setUserRole('staff');
            setStaffName('Staff Member');
          }
        } catch (err) {
          console.error("Failed to lookup staff role on session restore:", err);
          setUserRole('staff');
          setStaffName('Staff Member');
        }
      } else {
        setIsAuthenticated(false);
        setUserRole(null);
        setStaffName('');
      }
    });
    return () => unsubscribe();
  }, []);

  const loadStaffMembers = async () => {
    setIsLoadingStaff(true);
    try {
      const members = await dataService.getStaffMembers();
      setStaffMembers(members);
    } catch (err) {
      console.error("Failed to load staff:", err);
    } finally {
      setIsLoadingStaff(false);
    }
  };

  useEffect(() => {
    if (isAuthenticated && userRole === 'admin') {
      loadStaffMembers();
    }
  }, [isAuthenticated, userRole]);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setAuthError(null);
    setAuthLoading(true);

    const targetEmail = email.trim().toLowerCase();

    try {
      // First, attempt standard sign-in
      const userCredential = await signInWithEmailAndPassword(auth, targetEmail, password);
      const user = userCredential.user;
      
      try {
        const staffRecord = user.email ? await dataService.getStaffMemberByEmail(user.email) : undefined;
        if (staffRecord) {
          setUserRole(staffRecord.role as UserRole);
          setStaffName(staffRecord.name);
        } else if (user.email === 'nassoroa@gmail.com' || user.email?.includes('admin') || loginRole === 'admin') {
          setUserRole('admin');
          setStaffName('Owner');
        } else {
          setUserRole('staff');
          setStaffName('Staff Member');
        }
      } catch (roleErr) {
        console.error("Failed to fetch staff record:", roleErr);
        setUserRole('staff');
        setStaffName('Staff Member');
      }
      setIsAuthenticated(true);
    } catch (err: any) {
      console.error(err);
      
      // Auto-registration trigger:
      // If the email is nassoroa@gmail.com, and they typed the specified password, 
      // but it doesn't exist yet, automatically provision it on the fly!
      if (targetEmail === 'nassoroa@gmail.com' && password === 'Zakia@01)(*&$') {
        try {
          const userCredential = await createUserWithEmailAndPassword(auth, targetEmail, password);
          const user = userCredential.user;
          setUserRole('admin');
          setStaffName('Owner');
          setIsAuthenticated(true);
          return;
        } catch (createErr: any) {
          console.error("Auto-provisioning failed:", createErr);
        }
      }

      let errorMsg = "Access denied. Please check your credentials.";
      if (err.code === 'auth/invalid-credential' || err.code === 'auth/wrong-password' || err.code === 'auth/user-not-found') {
        errorMsg = "Invalid email or password. Please try again.";
      } else if (err.code === 'auth/invalid-email') {
        errorMsg = "Please enter a valid email address.";
      } else if (err.code === 'auth/too-many-requests') {
        errorMsg = "Too many failed login attempts. Please try again later.";
      }
      setAuthError(errorMsg);
    } finally {
      setAuthLoading(false);
    }
  };

  const handleForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setAuthError(null);
    setAuthLoading(true);
    setResetSuccess(false);

    try {
      await sendPasswordResetEmail(auth, resetEmail);
      setResetSuccess(true);
    } catch (err: any) {
      console.error(err);
      let errorMsg = "Failed to send password reset email. Please try again.";
      if (err.code === 'auth/invalid-email') {
        errorMsg = "Please enter a valid email address.";
      } else if (err.code === 'auth/user-not-found') {
        errorMsg = "No account found with this email.";
      }
      setAuthError(errorMsg);
    } finally {
      setAuthLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      await signOut(auth);
      setIsAuthenticated(false);
      setUserRole(null);
    } catch (err) {
      console.error("Logout failed:", err);
    }
  };

  const handleCreateStaff = async (e: React.FormEvent) => {
    e.preventDefault();
    setStaffError(null);
    setStaffSuccess(null);

    const emailVal = newStaffEmail.trim().toLowerCase();
    const nameVal = newStaffName.trim();
    const passwordVal = newStaffPassword;

    if (!nameVal || !emailVal || !passwordVal) {
      setStaffError("Please fill out all fields.");
      return;
    }

    if (passwordVal.length < 6) {
      setStaffError("Password must be at least 6 characters.");
      return;
    }

    setIsLoadingStaff(true);
    try {
      // Check if temporary app is already cached
      let tempApp;
      try {
        tempApp = getApp("TempAppCreator");
      } catch {
        tempApp = initializeApp({
          apiKey: "AIzaSyB-tNqWUX6onkA1hwzgRzcam7WiERGVSW0",
          authDomain: "dakar1.firebaseapp.com",
          projectId: "dakar1",
          storageBucket: "dakar1.firebasestorage.app",
          messagingSenderId: "908496794258",
          appId: "1:908496794258:web:c2e085f281e5aff6d22c4b",
          measurementId: "G-WP1H12Y395"
        }, "TempAppCreator");
      }
      
      const tempAuth = getAuth(tempApp);
      
      // Register credentials with Auth (bypassing state of current user app session)
      await createUserWithEmailAndPassword(tempAuth, emailVal, passwordVal);
      await tempAuth.signOut();

      // Write role assignment mapping record into Firestore
      await dataService.addStaffMember({
        name: nameVal,
        email: emailVal,
        role: newStaffRole,
        createdAt: new Date().toISOString()
      });

      setStaffSuccess(`Staff member "${nameVal}" has been registered successfully!`);
      setNewStaffName('');
      setNewStaffEmail('');
      setNewStaffPassword('');
      setNewStaffRole('sales_manager');
      setIsAddingStaff(false);
      
      // Reload staff lists for instantaneous interface update
      await loadStaffMembers();
    } catch (err: any) {
      console.error(err);
      let msg = "Failed to register staff member.";
      if (err.code === 'auth/email-already-in-use') {
        // If they already exist in Firebase Auth but are NOT present in active Firestore roster, they were previously deleted.
        // We can re-add them, and trigger a password reset email so they can securely change/set their password.
        const alreadyActive = staffMembers.some(m => m.email?.toLowerCase() === emailVal);
        if (!alreadyActive) {
          try {
            await dataService.addStaffMember({
              name: nameVal,
              email: emailVal,
              role: newStaffRole,
              createdAt: new Date().toISOString()
            });

            try {
              await sendPasswordResetEmail(auth, emailVal);
              setStaffSuccess(`Staff member "${nameVal}" has been successfully re-added to the roster! Since this email was registered previously, we have re-assigned their access role and sent a secure link to ${emailVal} to change their password.`);
            } catch (resetErr: any) {
              console.error("Failed to send reset link:", resetErr);
              setStaffSuccess(`Staff member "${nameVal}" has been successfully re-added to the roster with their old account login. (Note: Password reset link could not be sent automatically, but they can sign in or use standard password recovery).`);
            }

            setNewStaffName('');
            setNewStaffEmail('');
            setNewStaffPassword('');
            setNewStaffRole('sales_manager');
            setIsAddingStaff(false);
            await loadStaffMembers();
            return;
          } catch (dbErr: any) {
            console.error(dbErr);
            msg = "Failed to restore staff profile to the database.";
          }
        } else {
          msg = "An active staff member with this email address already exists in the roster.";
        }
      } else if (err.code === 'auth/invalid-email') {
        msg = "The provided email address format is invalid.";
      } else if (err.code === 'auth/weak-password') {
        msg = "The chosen password does not meet basic password strength rules.";
      }
      setStaffError(msg);
    } finally {
      setIsLoadingStaff(false);
    }
  };

  const handleStartEditStaff = (member: any) => {
    setEditingStaffId(member.id);
    setNewStaffName(member.name);
    setNewStaffEmail(member.email);
    setNewStaffRole(member.role);
    setStaffError(null);
    setStaffSuccess(null);
  };

  const handleCancelEditStaff = () => {
    setEditingStaffId(null);
    setNewStaffName('');
    setNewStaffEmail('');
    setNewStaffPassword('');
    setNewStaffRole('sales_manager');
    setStaffError(null);
    setStaffSuccess(null);
  };

  const handleSaveStaff = async (e: React.FormEvent) => {
    e.preventDefault();
    setStaffError(null);
    setStaffSuccess(null);

    const nameVal = newStaffName.trim();
    if (!nameVal) {
      setStaffError("Please enter a valid name.");
      return;
    }

    setIsLoadingStaff(true);
    try {
      if (editingStaffId) {
        await dataService.updateStaffMember(editingStaffId, {
          name: nameVal,
          role: newStaffRole
        });
        setStaffSuccess(`Staff member details updated successfully!`);
        handleCancelEditStaff();
        await loadStaffMembers();
      }
    } catch (err: any) {
      console.error(err);
      setStaffError("Failed to update staff member.");
    } finally {
      setIsLoadingStaff(false);
    }
  };

  const handleSendStaffPasswordReset = async () => {
    if (!newStaffEmail) return;
    setIsLoadingStaff(true);
    setStaffError(null);
    setStaffSuccess(null);
    try {
      await sendPasswordResetEmail(auth, newStaffEmail.trim());
      setStaffSuccess(`A secure password reset email has been dispatched to ${newStaffEmail}.`);
    } catch (err: any) {
      console.error(err);
      setStaffError("Failed to dispatch password reset email. Please verify the email is correct.");
    } finally {
      setIsLoadingStaff(false);
    }
  };

  const handleDeleteStaff = async (id: string, name: string) => {
    if (!confirm(`Are you sure you want to remove ${name} from your staff? They will lose write/edit permissions immediately.`)) {
      return;
    }
    
    setIsLoadingStaff(true);
    try {
      await dataService.deleteStaffMember(id);
      setStaffSuccess(`Staff member "${name}" was deleted successfully.`);
      await loadStaffMembers();
    } catch (err) {
      console.error(err);
      setStaffError("Failed to remove the staff member from database.");
    } finally {
      setIsLoadingStaff(false);
    }
  };

  const handleChangePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setPasswordError(null);
    setPasswordSuccess(null);

    const newPass = newPasswordValue.trim();
    if (!newPass || newPass.length < 6) {
      setPasswordError("Password must be at least 6 characters.");
      return;
    }

    setIsChangingPassword(true);
    try {
      const user = auth.currentUser;
      if (user) {
        await updatePassword(user, newPass);
        setPasswordSuccess("Password updated successfully! Write down your new password.");
        setNewPasswordValue('');
      } else {
        setPasswordError("No active login session detected. Please re-authenticate.");
      }
    } catch (err: any) {
      console.error(err);
      let msg = "Failed to change password.";
      if (err.code === 'auth/requires-recent-login') {
        msg = "For security reasons, changing your password requires a recent login. Please sign out and sign back in to perform this operation.";
      }
      setPasswordError(msg);
    } finally {
      setIsChangingPassword(false);
    }
  };

  const loadData = async () => {
    setIsLoadingData(true);
    try {
      const [c, r, t, s] = await Promise.all([
        dataService.getCars(),
        dataService.getCarRequests(),
        dataService.getTestDrives(),
        dataService.getSiteSettings()
      ]);
      setCars(c);
      setRequests(r);
      setTestDrives(t);
      setUnderConstruction(s.underConstruction);
    } catch (err) {
      console.error("Data Load Error:", err);
    } finally {
      setIsLoadingData(false);
    }
  };

  const handleToggleUnderConstruction = async () => {
    if (userRole !== 'admin') return;
    const nextVal = !underConstruction;
    try {
      setIsLoadingData(true);
      await dataService.updateSiteSettings({ underConstruction: nextVal });
      setUnderConstruction(nextVal);
      window.dispatchEvent(new CustomEvent('site-settings-updated', { detail: { underConstruction: nextVal } }));
      alert(`Website status successfully modified. The site is now ${nextVal ? 'UNDER CONSTRUCTION' : 'LIVE'}.`);
    } catch (err) {
      console.error("Failed to update site settings:", err);
      alert("Failed to update website status.");
    } finally {
      setIsLoadingData(false);
    }
  };

  useEffect(() => {
    if (isAuthenticated) loadData();
  }, [isAuthenticated]);

  const handleClearDatabase = async () => {
    if (userRole !== 'admin') return;
    if (!confirm("CRITICAL: This will delete ALL current vehicle listings. This action is irreversible. Proceed?")) return;
    
    setIsLoadingData(true);
    try {
      const currentCars = await dataService.getCars();
      for (const car of currentCars) {
        await dataService.deleteCar(car.id);
      }
      alert("Inventory catalog successfully cleared.");
      loadData();
    } catch (err) {
      alert("Wipe operation failed. Check connection.");
    } finally {
      setIsLoadingData(false);
    }
  };

  const handleSeedDatabase = async () => {
    if (userRole !== 'admin') return;
    if (!confirm("This will populate your database with 16 professional imaginary vehicle listings (2008-2021). Continue?")) return;
    
    setIsSeeding(true);
    const seedData: Omit<Car, 'id'>[] = [
      {
        make: "Nebula", model: "Interceptor 5", year: 2021, price: 68500, mileage: 4200, color: "Obsidian Void", vin: "NB21INT55X0001",
        description: "Zero-emissions luxury performance. Features the patented 'Gravity-Drive' suspension system for a cloud-like ride.",
        features: ["Neuro-Link HUD", "AI Copilot 'Nia'", "Solar-Reactive Paint", "Magnetic Brakes"],
        images: [
          "https://images.unsplash.com/photo-1503376780353-7e6692767b70?q=80&w=800",
          "https://images.unsplash.com/photo-1494976388531-d1058494cdd8?q=80&w=800",
          "https://images.unsplash.com/photo-1542362567-b055002b91f4?q=80&w=800",
          "https://images.unsplash.com/photo-1552519507-da3b142c6e3d?q=80&w=800"
        ],
        status: CarStatus.AVAILABLE, featured: true, category: 'Luxury'
      },
      {
        make: "Zorya", model: "Horizon S", year: 2019, price: 42000, mileage: 18500, color: "Stellar Copper", vin: "ZY19HORZ99S0002",
        description: "The ultimate adventure SUV. Lightweight titanium alloy frame with multi-terrain sensory adaptation.",
        features: ["360 Thermal View", "Self-Cleaning Glass", "Bio-Metric Locks", "Hover-Assist Ready"],
        images: [
          "https://images.unsplash.com/photo-1533473359331-0135ef1b58bf?q=80&w=800",
          "https://images.unsplash.com/photo-1549317661-bd32c8ce0db2?q=80&w=800",
          "https://images.unsplash.com/photo-1563720223185-11003d516935?q=80&w=800",
          "https://images.unsplash.com/photo-1502877338535-766e1452684a?q=80&w=800"
        ],
        status: CarStatus.AVAILABLE, featured: true, category: 'SUV'
      },
      {
        make: "Vesper", model: "Goliath HD", year: 2020, price: 58900, mileage: 12000, color: "Arctic Slate", vin: "VP20GOL99H0003",
        description: "Heavy-duty electric hauler. Massive torque with a reinforced carbon-nanotube bed.",
        features: ["Payload Auto-Balance", "Off-Grid Charging", "External Power Outlets", "Quad-Motor Drive"],
        images: [
          "https://images.unsplash.com/photo-1583121274602-3e2820c69888?q=80&w=800",
          "https://images.unsplash.com/photo-1591860454448-5813cca8f615?q=80&w=800",
          "https://images.unsplash.com/photo-1605559424843-9e4c228bf1c2?q=80&w=800",
          "https://images.unsplash.com/photo-1533106497176-45ae19e68ba2?q=80&w=800"
        ],
        status: CarStatus.AVAILABLE, featured: true, category: 'Truck'
      },
      {
        make: "Aethon", model: "Stratus Coupe", year: 2017, price: 31500, mileage: 45000, color: "Liquid Mercury", vin: "AT17STRT77C0004",
        description: "Elegance meets speed. A classic grand tourer with modern hydrogen-cell technology.",
        features: ["Active Aerodynamics", "Leather & Bamboo Interior", "Voice-Gestures", "Safe-Stop V2"],
        images: [
          "https://images.unsplash.com/photo-1544829099-b9a0c07fad1a?q=80&w=800",
          "https://images.unsplash.com/photo-1614162692292-7ac56d7f7f1e?q=80&w=800",
          "https://images.unsplash.com/photo-1553440569-bcc63803a83d?q=80&w=800",
          "https://images.unsplash.com/photo-1584345604476-8ec5e12e42dd?q=80&w=800"
        ],
        status: CarStatus.AVAILABLE, featured: false, category: 'Coupe'
      },
      {
        make: "Lumina", model: "Pulse SE", year: 2018, price: 22800, mileage: 33000, color: "Neon Cyan", vin: "LM18PULS11S0005",
        description: "The city's favorite compact. Highly efficient urban cruiser with a tiny parking footprint.",
        features: ["Auto-Park AI", "App-Sync Dash", "Smart Glass Tint", "Heated Steering"],
        images: [
          "https://images.unsplash.com/photo-1519641471654-76ce0107ad1b?q=80&w=800",
          "https://images.unsplash.com/photo-1541348263662-e0c8de4259ba?q=80&w=800",
          "https://images.unsplash.com/photo-1533473359331-0135ef1b58bf?q=80&w=800",
          "https://images.unsplash.com/photo-1621007947382-bb3c3994e3fb?q=80&w=800"
        ],
        status: CarStatus.AVAILABLE, featured: false, category: 'Sedan'
      }
    ];

    try {
      for (const car of seedData) {
        await dataService.addCar(car);
      }
      alert("Inventory Seeded Successfully! Premium vehicle catalog expanded.");
      loadData();
    } catch (err) {
      alert("Seeding failed. Check console for details.");
    } finally {
      setIsSeeding(false);
    }
  };

  const handleDeleteCar = async (id: string) => {
    if (userRole !== 'admin') {
      alert("Unauthorized: Administrative credentials required for purge.");
      return;
    }
    if (confirm('Are you sure you want to delete this vehicle? This action is permanent.')) {
      try {
        await dataService.deleteCar(id);
        loadData();
      } catch (err) {
        alert("Delete operation failed.");
      }
    }
  };

  const handleEditCar = (car: Car) => {
    setEditingCarId(car.id);
    setIsAddingCar(true);
    setUploadedImages(car.images || []);
    reset({
      make: car.make,
      model: car.model,
      year: car.year,
      price: car.price,
      mileage: car.mileage,
      color: car.color,
      vin: car.vin,
      description: car.description,
      features: (car.features || []).join(', ') as any,
      featured: car.featured,
      category: car.category,
      status: car.status
    });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleImageChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;
    setIsUploading(true);
    try {
      const processedImagesPromises = Array.from(files).map((file: File) => compressImage(file));
      const compressedResults = await Promise.all(processedImagesPromises);
      setUploadedImages((prev) => [...prev, ...compressedResults]);
    } catch (err) {
      console.error("Error processing images:", err);
      alert("Image processing failed. Try smaller files.");
    } finally {
      setIsUploading(false);
      e.target.value = '';
    }
  };

  const removeImage = (index: number) => {
    setUploadedImages((prev) => prev.filter((_, i) => i !== index));
  };

  const moveImage = (index: number, direction: 'left' | 'right') => {
    const newImages = [...uploadedImages];
    const targetIdx = direction === 'left' ? index - 1 : index + 1;
    if (targetIdx < 0 || targetIdx >= newImages.length) return;
    [newImages[index], newImages[targetIdx]] = [newImages[targetIdx], newImages[index]];
    setUploadedImages(newImages);
  };

  // Drag and drop handlers
  const onDragStart = (idx: number) => {
    setDraggedIdx(idx);
  };

  const onDragOver = (e: React.DragEvent) => {
    e.preventDefault(); // Necessary to allow dropping
  };

  const onDrop = (targetIdx: number) => {
    if (draggedIdx === null || draggedIdx === targetIdx) return;
    const newImages = [...uploadedImages];
    const item = newImages.splice(draggedIdx, 1)[0];
    newImages.splice(targetIdx, 0, item);
    setUploadedImages(newImages);
    setDraggedIdx(null);
  };

  const handleSaveCar = async (data: Partial<Car>) => {
    if (userRole !== 'admin') return;
    if (uploadedImages.length === 0) {
      alert("Please upload at least one image.");
      return;
    }

    try {
      const carData: Omit<Car, 'id'> = {
        make: data.make || '',
        model: data.model || '',
        year: Number(data.year),
        price: Number(data.price),
        mileage: Number(data.mileage),
        color: data.color || '',
        vin: data.vin || '',
        description: data.description || '',
        features: (data.features as any as string)?.split(',').map((f: string) => f.trim()).filter(Boolean) || [],
        images: uploadedImages,
        featured: data.featured || false,
        status: data.status || CarStatus.AVAILABLE,
        category: (data.category as any) || 'Sedan'
      };
      
      if (editingCarId) {
        await dataService.updateCar(editingCarId, carData);
        alert("Listing updated successfully.");
      } else {
        await dataService.addCar(carData);
        alert("New listing published.");
      }

      setIsAddingCar(false);
      setEditingCarId(null);
      setUploadedImages([]);
      reset();
      loadData();
    } catch (error: any) {
      console.error(error);
      if (error.message?.includes('exceeds the maximum allowed size')) {
        alert("CRITICAL ERROR: Document size limit reached. Reduce image count or resolution.");
      } else {
        alert("Sync failed. Check connection.");
      }
    }
  };

  if (!isAuthenticated) {
    return (
      <div className="min-h-[calc(100vh-7rem)] bg-brandGray flex items-center justify-center px-4 py-20">
        <div className="bg-white p-10 rounded-3xl shadow-2xl w-full max-w-md animate-fade-in border-t-[12px] border-primary">
          <div className="flex justify-center mb-8">
            <div className={`p-5 rounded-full ${loginRole === 'admin' ? 'bg-danger/10' : 'bg-primary/10'}`}>
              {isForgotPassword ? (
                <Mail size={48} className={loginRole === 'admin' ? 'text-danger animate-pulse' : 'text-primary animate-pulse'} />
              ) : loginRole === 'admin' ? (
                <ShieldCheck size={48} className="text-danger" />
              ) : (
                <Briefcase size={48} className="text-primary" />
              )}
            </div>
          </div>
          
          <h2 className="text-3xl font-heading font-bold text-brandDark mb-1 text-center uppercase tracking-tight">
            {isForgotPassword ? 'Reset Password' : 'Staff Portal'}
          </h2>
          <p className="text-gray-400 text-center mb-8 text-[10px] uppercase tracking-widest font-bold">
            {isForgotPassword ? 'Receive password recovery instructions' : 'Authorized Personnel Only'}
          </p>
          
          {authError && (
            <div className="bg-red-50 text-danger p-4 rounded-2xl text-xs font-bold border border-red-100 flex items-center gap-2 mb-6">
              <AlertTriangle size={16} className="flex-shrink-0" />
              <span>{authError}</span>
            </div>
          )}

          {resetSuccess && (
            <div className="bg-emerald-50 text-emerald-800 p-4 rounded-2xl text-xs font-bold border border-emerald-100 flex items-center gap-2 mb-6">
              <Check size={16} className="flex-shrink-0" />
              <span>Reset link transmitted successfully! Check your email inbox.</span>
            </div>
          )}

          {!isForgotPassword ? (
            <form onSubmit={handleLogin} className="space-y-6">
              <div className="grid grid-cols-2 gap-2 p-1 bg-brandGray rounded-xl mb-6">
                <button 
                  type="button"
                  onClick={() => setLoginRole('staff')}
                  className={`py-2 text-[10px] font-bold uppercase rounded-lg transition-all ${loginRole === 'staff' ? 'bg-white text-primary shadow-sm' : 'text-gray-400'}`}
                >
                  Office Staff
                </button>
                <button 
                  type="button"
                  onClick={() => setLoginRole('admin')}
                  className={`py-2 text-[10px] font-bold uppercase rounded-lg transition-all ${loginRole === 'admin' ? 'bg-white text-danger shadow-sm' : 'text-gray-400'}`}
                >
                  Admin / Owner
                </button>
              </div>

              <div>
                <label className="block text-[10px] font-bold uppercase tracking-widest text-gray-400 mb-2">
                  Email Address
                </label>
                <div className="relative">
                  <input 
                    type="email" 
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="user@dakarautosales.com"
                    className="w-full bg-brandGray border border-gray-200 rounded-2xl px-5 py-4 text-brandDark focus:ring-2 focus:ring-primary focus:border-transparent transition-all outline-none pr-12 text-sm font-bold"
                  />
                  <Mail className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-300" size={18} />
                </div>
              </div>

              <div>
                <div className="flex justify-between items-center mb-2">
                  <label className="block text-[10px] font-bold uppercase tracking-widest text-gray-400">
                    Secure Password
                  </label>
                  <button 
                    type="button"
                    onClick={() => {
                      setIsForgotPassword(true);
                      setResetEmail(email);
                      setAuthError(null);
                      setResetSuccess(false);
                    }}
                    className="text-[9px] font-bold uppercase tracking-widest text-primary hover:underline outline-none"
                  >
                    Forgot Password?
                  </button>
                </div>
                <div className="relative">
                  <input 
                    type="password" 
                    required
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="••••••••"
                    className="w-full bg-brandGray border border-gray-200 rounded-2xl px-5 py-4 text-brandDark focus:ring-2 focus:ring-primary focus:border-transparent transition-all outline-none pr-12 text-sm"
                  />
                  <Lock className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-300" size={18} />
                </div>
              </div>

              <button 
                type="submit" 
                disabled={authLoading}
                className={`w-full text-white font-heading font-bold py-4 rounded-2xl uppercase tracking-[0.2em] transition-all shadow-xl hover:-translate-y-1 active:translate-y-0 disabled:opacity-50 disabled:pointer-events-none flex items-center justify-center gap-2 ${loginRole === 'admin' ? 'bg-brandDark hover:bg-danger' : 'bg-brandDark hover:bg-primary'}`}
              >
                {authLoading ? (
                  <>
                    <div className="h-4 w-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    Authenticating...
                  </>
                ) : (
                  'Initialize Session'
                )}
              </button>
            </form>
          ) : (
            <form onSubmit={handleForgotPassword} className="space-y-6">
              <div>
                <label className="block text-[10px] font-bold uppercase tracking-widest text-gray-400 mb-2">
                  Account Email Address
                </label>
                <div className="relative">
                  <input 
                    type="email" 
                    required
                    value={resetEmail}
                    onChange={(e) => setResetEmail(e.target.value)}
                    placeholder="user@dakarautosales.com"
                    className="w-full bg-brandGray border border-gray-200 rounded-2xl px-5 py-4 text-brandDark focus:ring-2 focus:ring-primary focus:border-transparent transition-all outline-none pr-12 text-sm font-bold"
                  />
                  <Mail className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-300" size={18} />
                </div>
              </div>

              <button 
                type="submit" 
                disabled={authLoading}
                className="w-full text-white bg-brandDark hover:bg-primary font-heading font-bold py-4 rounded-2xl uppercase tracking-[0.15em] transition-all shadow-xl hover:-translate-y-1 active:translate-y-0 disabled:opacity-50 disabled:pointer-events-none flex items-center justify-center gap-2"
              >
                {authLoading ? (
                  <>
                    <div className="h-4 w-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    Transmitting...
                  </>
                ) : (
                  'Send Reset Link'
                )}
              </button>

              <button 
                type="button" 
                onClick={() => {
                  setIsForgotPassword(false);
                  setAuthError(null);
                  setResetSuccess(false);
                }}
                className="w-full text-[10px] text-gray-400 hover:text-brandDark font-bold uppercase tracking-widest text-center mt-2 transition-colors block"
              >
                ← Back to Staff Login
              </button>
            </form>
          )}

          <div className="mt-8 pt-6 border-t border-gray-100 text-center">
            <span className="text-[9px] text-gray-400 font-bold uppercase tracking-wider block leading-relaxed px-4">
              Authorized Dakar Auto Sales operators require their email credentials to be registered via Firebase Console.
            </span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-brandGray text-brandDark p-4 md:p-8 font-sans">
      <div className="max-w-7xl mx-auto">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-6 mb-10">
          <div className="flex items-center gap-4">
            <div className={`p-4 rounded-2xl shadow-lg text-white ${userRole === 'admin' ? 'bg-danger' : 'bg-primary'}`}>
              {userRole === 'admin' ? <ShieldCheck size={28} /> : <Briefcase size={28} />}
            </div>
            <div>
              <h1 className="text-3xl md:text-4xl font-heading font-bold uppercase tracking-tight flex items-center gap-3">
                Dakar <span className={userRole === 'admin' ? 'text-danger' : 'text-primary'}>Workspace</span>
              </h1>
              <div className="flex items-center gap-2 mt-1">
                <span className={`px-2 py-0.5 rounded text-[8px] font-bold uppercase tracking-[0.1em] ${
                  userRole === 'admin' ? 'bg-danger text-white' : 
                  userRole === 'sales_manager' ? 'bg-emerald-600 text-white' : 'bg-primary text-white'
                }`}>
                  {userRole === 'admin' ? 'Executive Access' : 
                   userRole === 'sales_manager' ? 'Sales Manager Access' : 'Operational Access'}
                </span>
                <span className="text-[9px] text-gray-500 font-bold uppercase tracking-widest">
                  • Welcome, <span className="text-brandDark font-extrabold">{staffName || 'Operator'}</span>
                </span>
              </div>
            </div>
          </div>
          
          <div className="flex flex-wrap gap-4">
            {userRole === 'admin' && (
              <>
                <button 
                  onClick={handleToggleUnderConstruction}
                  disabled={isLoadingData}
                  className={`px-6 py-3 rounded-2xl flex items-center gap-2.5 font-bold uppercase tracking-widest text-[10px] transition-all shadow-md border ${
                    underConstruction 
                      ? 'bg-amber-50 border-amber-200 text-amber-800 hover:bg-amber-100 shadow-amber-100/50' 
                      : 'bg-emerald-50 border-emerald-200 text-emerald-800 hover:bg-emerald-100 shadow-emerald-100/50'
                  }`}
                >
                  <span className={`h-2.5 w-2.5 rounded-full ${underConstruction ? 'bg-amber-500 animate-pulse' : 'bg-emerald-500'}`}></span>
                  <span>STATUS: {underConstruction ? 'UNDER CONSTRUCTION' : 'LIVE'}</span>
                </button>
                <button 
                  onClick={() => navigate('/database')}
                  className="bg-[#1a1c23] text-white hover:bg-emerald-800 px-6 py-3 rounded-2xl flex items-center gap-2 font-bold uppercase tracking-widest text-[10px] transition-all shadow-md flex items-center"
                >
                  <Flame size={16} className="text-[#FFCA28] fill-[#FFCA28]" /> Firebase & Excel Sync
                </button>
                <button 
                  onClick={handleClearDatabase}
                  disabled={isLoadingData}
                  className="bg-white border border-gray-200 text-gray-400 hover:text-danger hover:border-danger px-6 py-3 rounded-2xl flex items-center gap-2 font-bold uppercase tracking-widest text-[10px] transition-all shadow-sm"
                >
                  <AlertTriangle size={16} /> Wipe Catalog
                </button>
                <button 
                  onClick={handleSeedDatabase}
                  disabled={isSeeding}
                  className="bg-accent text-brandDark px-6 py-3 rounded-2xl flex items-center gap-2 font-bold uppercase tracking-widest text-[10px] transition-all shadow-sm hover:scale-105 active:scale-100 disabled:opacity-50"
                >
                  {isSeeding ? <Database size={16} className="animate-spin" /> : <Sparkles size={16} />} 
                  {isSeeding ? 'Populating...' : 'Seed Data'}
                </button>
              </>
            )}
            <button onClick={handleLogout} className="bg-white border border-gray-200 text-gray-400 hover:text-danger hover:border-danger px-6 py-3 rounded-2xl flex items-center gap-2 font-bold uppercase tracking-widest text-[10px] transition-all shadow-sm">
              <LogOut size={16} /> End Session
            </button>
          </div>
        </div>

        <div className="flex space-x-2 mb-8 bg-white p-1.5 rounded-2xl w-fit border border-gray-100 shadow-sm overflow-x-auto">
          <button onClick={() => setActiveTab('inventory')} className={`py-3 px-6 rounded-xl font-bold uppercase tracking-widest text-[9px] flex items-center gap-2.5 transition-all whitespace-nowrap ${activeTab === 'inventory' ? 'bg-brandDark text-white shadow-lg' : 'text-gray-400 hover:text-brandDark hover:bg-brandGray'}`}>
            <CarIcon size={14} /> Inventory
          </button>
          <button onClick={() => setActiveTab('requests')} className={`py-3 px-6 rounded-xl font-bold uppercase tracking-widest text-[9px] flex items-center gap-2.5 transition-all whitespace-nowrap ${activeTab === 'requests' ? 'bg-brandDark text-white shadow-lg' : 'text-gray-400 hover:text-brandDark hover:bg-brandGray'}`}>
            <User size={14} /> Leads
          </button>
          <button onClick={() => setActiveTab('testdrives')} className={`py-3 px-6 rounded-xl font-bold uppercase tracking-widest text-[9px] flex items-center gap-2.5 transition-all whitespace-nowrap ${activeTab === 'testdrives' ? 'bg-brandDark text-white shadow-lg' : 'text-gray-400 hover:text-brandDark hover:bg-brandGray'}`}>
            <Calendar size={14} /> Schedule
          </button>
          <button onClick={() => setActiveTab('staff')} className={`py-3 px-6 rounded-xl font-bold uppercase tracking-widest text-[9px] flex items-center gap-2.5 transition-all whitespace-nowrap ${activeTab === 'staff' ? 'bg-brandDark text-white shadow-lg' : 'text-gray-400 hover:text-brandDark hover:bg-brandGray'}`}>
            <Users size={14} /> {userRole === 'admin' ? 'Staff Control' : 'Profile Settings'}
          </button>
        </div>

        <div className="bg-white rounded-[2.5rem] border border-gray-100 p-6 md:p-10 shadow-2xl min-h-[600px] mb-20">
          {activeTab === 'inventory' && (
            <div className="animate-fade-in">
              <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-10">
                <div>
                  <h2 className="text-2xl font-heading font-bold uppercase tracking-wider text-brandDark">Units Control</h2>
                  <p className="text-[10px] text-gray-400 font-bold uppercase tracking-widest mt-1">Manage global vehicle listings</p>
                </div>
                <div className="flex flex-wrap items-center gap-3">
                  <button 
                    onClick={() => setIsExcelPanelOpen(!isExcelPanelOpen)}
                    className={`px-6 py-4 rounded-2xl font-bold uppercase text-[10px] tracking-widest flex items-center gap-2.5 transition-all shadow-sm border ${
                      isExcelPanelOpen 
                        ? 'bg-emerald-50 border-emerald-200 text-emerald-800 shadow-emerald-100/50' 
                        : 'bg-white border-gray-200 text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    <FileSpreadsheet size={18} className="text-emerald-700" />
                    Excel Board
                  </button>
                  {(userRole === 'admin' || userRole === 'sales_manager') && !isAddingCar && (
                    <button onClick={() => { setIsAddingCar(true); setEditingCarId(null); reset(); setUploadedImages([]); }} className="bg-primary hover:bg-primary/90 text-white px-8 py-4 rounded-2xl font-bold uppercase text-[10px] tracking-[0.2em] flex items-center gap-3 transition-all shadow-lg shadow-primary/20">
                      <Plus size={18} /> New Entry
                    </button>
                  )}
                </div>
              </div>

              {isExcelPanelOpen && (
                <div id="excel-panel" className="bg-emerald-50/40 p-8 rounded-3xl mb-12 border border-emerald-100/80 animate-fade-in shadow-sm">
                  <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-6 mb-8 border-b border-emerald-100 pb-6">
                    <div>
                      <h3 className="text-emerald-950 font-heading text-lg font-bold uppercase tracking-widest flex items-center gap-2">
                        <FileSpreadsheet size={22} className="text-emerald-700" />
                        Excel Spreadsheet Integration
                      </h3>
                      <p className="text-[10px] text-emerald-700 font-bold uppercase tracking-wider mt-1">
                        Download templates, export currently listed vehicles, or import listings in bulk
                      </p>
                    </div>
                    <button 
                      type="button"
                      onClick={() => {
                        setIsExcelPanelOpen(false);
                        setExcelImportCars(null);
                        setExcelImportErrors(null);
                        setExcelFileName('');
                      }} 
                      className="text-emerald-800 hover:text-danger bg-white/80 p-2 rounded-full shadow-sm hover:scale-105 transition-all"
                    >
                      <CloseIcon size={20} />
                    </button>
                  </div>

                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-stretch">
                    {/* LEFT PANEL: TEMPLATES & EXPORTS */}
                    <div className="bg-white p-6 rounded-2xl border border-emerald-100 flex flex-col justify-between">
                      <div>
                        <h4 className="text-[11px] font-bold uppercase tracking-[0.15em] text-emerald-950 mb-3">Download and Export Tools</h4>
                        <p className="text-xs text-gray-500 leading-relaxed mb-6">
                          Our pre-formatted spreadsheet contains the exact headers, datatypes, and structure verified by Dakar Auto Sales database. Populate this sheet and upload it on the right to import cars instantly.
                        </p>
                      </div>

                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <button 
                          type="button"
                          onClick={() => excelService.downloadTemplate()}
                          className="px-5 py-3.5 bg-brandDark hover:bg-emerald-950 text-white rounded-xl font-bold uppercase text-[9px] tracking-widest flex items-center justify-center gap-2 transition-all shadow-md"
                        >
                          <Download size={14} />
                          Get Blank Template
                        </button>
                        <button 
                          type="button"
                          onClick={() => excelService.exportToExcel(cars)}
                          className="px-5 py-3.5 bg-emerald-700 hover:bg-emerald-800 text-white rounded-xl font-bold uppercase text-[9px] tracking-widest flex items-center justify-center gap-2 transition-all shadow-md"
                        >
                          <FileSpreadsheet size={14} />
                          Export Lot Archive
                        </button>
                      </div>
                    </div>

                    {/* RIGHT PANEL: UPLOAD AND PARSE SPREADSHEETS */}
                    <div className="bg-white p-6 rounded-2xl border border-emerald-100">
                      <h4 className="text-[11px] font-bold uppercase tracking-[0.15em] text-emerald-950 mb-3">Upload Completed Spreadsheet</h4>
                      <div className="relative">
                        <label className="flex flex-col items-center justify-center border-2 border-dashed border-emerald-200/60 rounded-2xl cursor-pointer hover:border-emerald-500 hover:bg-emerald-50/10 transition-all p-8 group bg-brandGray/20">
                          <UploadCloud size={32} className="text-emerald-400 group-hover:text-emerald-600 transition-colors mb-2 animate-bounce" />
                          <span className="text-[10px] text-emerald-950 font-bold uppercase tracking-widest text-center">
                            {excelFileName ? excelFileName : 'Drag & Drop or Choose File'}
                          </span>
                          <span className="text-[8px] text-gray-400 uppercase tracking-widest mt-1">Supports .xlsx, .xls, .csv</span>
                          <input 
                            type="file" 
                            accept=".xlsx, .xls, .csv" 
                            onChange={handleExcelUpload} 
                            className="hidden" 
                          />
                        </label>
                      </div>

                      {isProcessingExcel && (
                        <div className="flex items-center gap-2 text-emerald-700 mt-4 animate-pulse justify-center">
                          <div className="h-4 w-4 border-2 border-emerald-700 border-t-transparent rounded-full animate-spin"></div>
                          <span className="text-[9px] font-bold uppercase tracking-widest">Reading & verifying document...</span>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* IMPORT FEEDBACK & ACTIONS */}
                  {excelImportErrors && (
                    <div className="bg-red-50 border border-red-200 text-red-800 p-6 rounded-2xl mt-8">
                      <h5 className="font-heading font-bold text-[13px] uppercase tracking-wider text-red-950 mb-2 flex items-center gap-1.5">
                        <AlertTriangle size={16} className="text-red-600" />
                        Verification Failures Found
                      </h5>
                      <ul className="list-disc pl-5 text-xs text-red-700 space-y-1">
                        {excelImportErrors.map((err, idx) => (
                          <li key={idx}>{err}</li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {excelImportCars && excelImportCars.length > 0 && (
                    <div className="bg-white border border-emerald-200/80 p-6 rounded-2xl mt-8 shadow-sm">
                      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6 pb-4 border-b border-gray-100">
                        <div>
                          <h5 className="font-heading font-bold text-[14px] uppercase tracking-wider text-emerald-950 flex items-center gap-2">
                            <Check size={18} className="text-emerald-600" />
                            File Verified ({excelImportCars.length} Vehicle Records Loaded)
                          </h5>
                          <p className="text-[9px] text-gray-400 font-bold uppercase mt-1">Review listings parsed from spreadsheet before committing to the lot</p>
                        </div>
                        <div className="flex gap-2">
                          <button 
                            type="button"
                            onClick={() => handleApplyExcelImport('append')}
                            className="bg-emerald-600 hover:bg-emerald-700 text-white px-5 py-2.5 rounded-xl font-bold uppercase text-[9px] tracking-widest transition-all shadow-md"
                          >
                            Append to Catalog
                          </button>
                          <button 
                            type="button"
                            onClick={() => handleApplyExcelImport('replace')}
                            className="bg-brandDark hover:bg-danger text-white px-5 py-2.5 rounded-xl font-bold uppercase text-[9px] tracking-widest transition-all shadow-md"
                          >
                            Replace All Current Lot
                          </button>
                        </div>
                      </div>

                      {/* MINIFIED PREVIEW GRID */}
                      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 max-h-[300px] overflow-y-auto p-1 bg-brandGray/10 rounded-xl">
                        {excelImportCars.map((car, idx) => (
                          <div key={idx} className="bg-brandGray/30 p-4 rounded-xl border border-gray-100 flex items-center gap-3">
                            <div className="w-12 h-12 rounded-lg bg-gray-100 overflow-hidden flex-shrink-0">
                              <img src={car.images?.[0]} alt={car.model} className="w-full h-full object-cover" />
                            </div>
                            <div className="min-w-0">
                              <p className="text-xs text-brandDark font-bold truncate">{car.year} {car.make} {car.model}</p>
                              <div className="flex items-center gap-2 mt-0.5">
                                <span className="text-[10px] text-emerald-750 font-bold font-heading">${car.price.toLocaleString()}</span>
                                <span className="text-[8px] text-gray-400 uppercase font-bold">{car.mileage.toLocaleString()} mi</span>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                </div>
              )}

              {isAddingCar && (userRole === 'admin' || userRole === 'sales_manager') && (
                 <form onSubmit={handleSubmit(handleSaveCar)} className="bg-brandGray p-8 rounded-3xl mb-12 border border-gray-200 animate-fade-in shadow-inner">
                    <div className="flex justify-between items-center mb-10 border-b border-gray-200 pb-6">
                      <div className="flex items-center gap-3">
                        <div className="bg-white p-2 rounded-xl shadow-sm"><ImageIcon size={20} className="text-primary" /></div>
                        <h3 className="text-brandDark font-heading text-xl font-bold uppercase tracking-widest">
                          {editingCarId ? 'Edit Entry' : 'New Listing Draft'}
                        </h3>
                      </div>
                      <button type="button" onClick={() => { setIsAddingCar(false); setEditingCarId(null); setUploadedImages([]); reset(); }} className="text-gray-400 hover:text-danger transition-colors bg-white p-2 rounded-full shadow-sm">
                        <CloseIcon size={20} />
                      </button>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
                      <div>
                        <label className="block text-[9px] font-bold uppercase tracking-[0.2em] text-gray-400 mb-2">Manufacturer</label>
                        <input {...register('make', {required: true})} placeholder="e.g. BMW" className="w-full bg-white border border-gray-100 p-4 rounded-xl text-brandDark text-sm focus:ring-1 focus:ring-primary outline-none shadow-sm" />
                      </div>
                      <div>
                        <label className="block text-[9px] font-bold uppercase tracking-[0.2em] text-gray-400 mb-2">Model</label>
                        <input {...register('model', {required: true})} placeholder="e.g. M3" className="w-full bg-white border border-gray-100 p-4 rounded-xl text-brandDark text-sm focus:ring-1 focus:ring-primary outline-none shadow-sm" />
                      </div>
                      <div>
                        <label className="block text-[9px] font-bold uppercase tracking-[0.2em] text-gray-400 mb-2">Year</label>
                        <input {...register('year', {required: true, min: 1900})} placeholder="2024" type="number" className="w-full bg-white border border-gray-100 p-4 rounded-xl text-brandDark text-sm focus:ring-1 focus:ring-primary outline-none shadow-sm" />
                      </div>
                      <div>
                        <label className="block text-[9px] font-bold uppercase tracking-[0.2em] text-gray-400 mb-2">Price ($)</label>
                        <input {...register('price', {required: true, min: 0})} placeholder="45000" type="number" className="w-full bg-white border border-gray-100 p-4 rounded-xl text-brandDark text-sm focus:ring-1 focus:ring-primary outline-none shadow-sm" />
                      </div>
                    </div>
                    
                    <div className="mb-10 p-6 bg-white border border-gray-100 rounded-3xl shadow-sm">
                      <div className="flex items-center justify-between mb-8">
                        <div>
                          <label className="block text-[10px] font-bold uppercase tracking-[0.2em] text-primary">Media Gallery</label>
                          <p className="text-[8px] text-gray-400 font-bold uppercase mt-1">Drag to reorder. The first image is the main cover.</p>
                        </div>
                        <span className="bg-brandGray px-3 py-1 rounded-full text-[9px] font-bold text-gray-500 uppercase tracking-widest">{uploadedImages.length} Assets</span>
                      </div>
                      
                      <div className="grid grid-cols-2 sm:grid-cols-4 md:grid-cols-6 lg:grid-cols-8 gap-4 mb-6">
                        {uploadedImages.map((img, idx) => (
                          <div 
                            key={idx} 
                            draggable
                            onDragStart={() => onDragStart(idx)}
                            onDragOver={onDragOver}
                            onDrop={() => onDrop(idx)}
                            className={`relative aspect-square rounded-2xl border border-gray-100 overflow-hidden group shadow-md bg-brandGray transition-all cursor-move ${draggedIdx === idx ? 'opacity-30' : 'opacity-100'}`}
                          >
                            <img src={img} alt={`Asset ${idx}`} className="w-full h-full object-cover transition-transform group-hover:scale-110 duration-500" />
                            <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center flex-col gap-2 p-2">
                              <div className="flex gap-2">
                                <button type="button" onClick={() => moveImage(idx, 'left')} className="bg-white p-1 rounded-lg text-brandDark hover:text-primary disabled:opacity-30" disabled={idx === 0}><ArrowLeft size={14}/></button>
                                <button type="button" onClick={() => moveImage(idx, 'right')} className="bg-white p-1 rounded-lg text-brandDark hover:text-primary disabled:opacity-30" disabled={idx === uploadedImages.length - 1}><ArrowRight size={14}/></button>
                              </div>
                              <button type="button" onClick={() => removeImage(idx)} className="bg-danger p-2 rounded-xl text-white shadow-xl"><Trash2 size={16} /></button>
                            </div>
                            <div className={`absolute bottom-0 left-0 right-0 py-1 text-[7px] text-center font-bold uppercase tracking-[0.2em] text-white ${idx === 0 ? 'bg-primary' : 'bg-brandDark/80'}`}>
                              {idx === 0 ? 'MAIN COVER' : <Move size={8} className="inline mx-auto"/>}
                            </div>
                          </div>
                        ))}
                        
                        <label className="aspect-square flex flex-col items-center justify-center border-2 border-dashed border-gray-200 rounded-2xl cursor-pointer hover:border-primary hover:bg-primary/5 transition-all group bg-brandGray/50">
                          <Upload size={24} className="text-gray-300 group-hover:text-primary transition-colors mb-2" />
                          <span className="text-[8px] text-gray-400 group-hover:text-primary font-bold uppercase tracking-widest text-center px-4">Upload Asset</span>
                          <input type="file" multiple accept="image/*" onChange={handleImageChange} className="hidden" />
                        </label>
                      </div>
                      
                      {isUploading && (
                        <div className="flex items-center gap-3 text-primary animate-pulse">
                           <div className="h-3 w-3 border-2 border-primary border-t-transparent rounded-full animate-spin"></div>
                           <span className="text-[9px] font-bold uppercase tracking-[0.3em]">Processing...</span>
                        </div>
                      )}
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
                       <div>
                        <label className="block text-[9px] font-bold uppercase tracking-[0.2em] text-gray-400 mb-2">Category</label>
                        <select {...register('category', {required: true})} className="w-full bg-white border border-gray-100 p-4 rounded-xl text-brandDark text-sm focus:ring-1 focus:ring-primary outline-none cursor-pointer shadow-sm">
                          <option value="Sedan">Sedan</option>
                          <option value="SUV">SUV</option>
                          <option value="Truck">Truck</option>
                          <option value="Coupe">Coupe</option>
                          <option value="Luxury">Luxury</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-[9px] font-bold uppercase tracking-[0.2em] text-gray-400 mb-2">Mileage (MI)</label>
                        <input {...register('mileage', {required: true, min: 0})} placeholder="15000" type="number" className="w-full bg-white border border-gray-100 p-4 rounded-xl text-brandDark text-sm focus:ring-1 focus:ring-primary outline-none shadow-sm" />
                      </div>
                      <div>
                        <label className="block text-[9px] font-bold uppercase tracking-[0.2em] text-gray-400 mb-2">Status</label>
                        <select {...register('status')} className="w-full bg-white border border-gray-100 p-4 rounded-xl text-brandDark text-sm focus:ring-1 focus:ring-primary outline-none cursor-pointer shadow-sm">
                          <option value={CarStatus.AVAILABLE}>Available</option>
                          <option value={CarStatus.PENDING}>Pending</option>
                          <option value={CarStatus.SOLD}>Sold</option>
                        </select>
                      </div>
                       <div className="flex items-center pt-8">
                         <label className="flex items-center gap-3 cursor-pointer group">
                            <input type="checkbox" {...register('featured')} className="w-5 h-5 text-primary rounded-lg focus:ring-primary border-gray-200 cursor-pointer" />
                            <span className="text-[10px] font-bold uppercase tracking-widest text-brandDark group-hover:text-primary transition-colors">Featured</span>
                         </label>
                      </div>
                    </div>

                    <div className="flex justify-end gap-4 mt-10 pt-8 border-t border-gray-200">
                      <button type="button" onClick={() => { setIsAddingCar(false); setEditingCarId(null); setUploadedImages([]); reset(); }} className="px-8 py-4 text-gray-400 font-bold uppercase text-[9px] tracking-[0.2em] hover:text-danger transition-colors">Cancel</button>
                      <button type="submit" disabled={isUploading || uploadedImages.length === 0} className="px-12 py-4 bg-brandDark text-white rounded-2xl font-bold uppercase text-[10px] tracking-[0.3em] hover:bg-primary transition-all shadow-xl disabled:opacity-20">
                        {editingCarId ? 'Update Record' : 'Save Listing'}
                      </button>
                    </div>
                 </form>
              )}

              <div className="overflow-x-auto rounded-[2rem] border border-gray-100 shadow-sm bg-white">
                <table className="w-full text-left text-gray-600 border-collapse">
                  <thead className="bg-brandGray/50 text-[9px] uppercase font-bold tracking-[0.2em]">
                    <tr>
                      <th className="px-8 py-6 border-b border-gray-50">Identity</th>
                      <th className="px-8 py-6 border-b border-gray-50">Price</th>
                      <th className="px-8 py-6 border-b border-gray-50">Miles</th>
                      <th className="px-8 py-6 border-b border-gray-50">Status</th>
                      <th className="px-8 py-6 border-b border-gray-50 text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-50">
                    {isLoadingData ? (
                      <tr><td colSpan={5} className="px-8 py-20 text-center"><div className="h-6 w-6 border-2 border-primary border-t-transparent rounded-full animate-spin mx-auto"></div></td></tr>
                    ) : cars.length === 0 ? (
                      <tr><td colSpan={5} className="px-8 py-24 text-center font-bold uppercase text-xs text-gray-300">Lot Empty</td></tr>
                    ) : (
                      cars.map(car => (
                        <tr key={car.id} className="group hover:bg-brandGray/20 transition-all">
                          <td className="px-8 py-6">
                            <div className="flex items-center gap-4">
                              <div className="w-16 h-16 rounded-xl overflow-hidden border border-gray-100 bg-brandGray flex-shrink-0 shadow-sm">
                                {car.images && car.images[0] ? (
                                  <img src={car.images[0]} alt={car.model} className="w-full h-full object-cover" />
                                ) : (
                                  <div className="w-full h-full flex items-center justify-center"><ImageIcon size={18} className="text-gray-200" /></div>
                                )}
                              </div>
                              <div className="flex flex-col">
                                <span className="text-brandDark font-bold text-sm tracking-tight">{car.year} {car.make} {car.model}</span>
                                <span className="text-[8px] text-primary font-bold uppercase tracking-widest mt-1 bg-primary/5 px-2 py-0.5 rounded w-fit">{car.category}</span>
                              </div>
                            </div>
                          </td>
                          <td className="px-8 py-6 font-heading font-bold text-brandDark">${car.price.toLocaleString()}</td>
                          <td className="px-8 py-6 text-xs text-gray-500 font-bold">{car.mileage.toLocaleString()} mi</td>
                          <td className="px-8 py-6">
                            <span className={`px-4 py-1.5 rounded-xl text-[9px] font-bold uppercase tracking-widest border inline-flex items-center gap-1.5 ${
                              car.status === CarStatus.AVAILABLE ? 'bg-primary text-white border-primary' : 
                              car.status === CarStatus.PENDING ? 'bg-accent text-brandDark border-accent' : 'bg-danger text-white border-danger'
                            }`}>
                              {car.status}
                            </span>
                          </td>
                          <td className="px-8 py-6 text-right">
                            <div className="flex justify-end gap-2">
                              {(userRole === 'admin' || userRole === 'sales_manager') && (
                                <button onClick={() => handleEditCar(car)} className="p-3 bg-white text-gray-300 hover:text-primary hover:border-primary rounded-xl border border-gray-100 transition-all"><Edit size={16} /></button>
                              )}
                              {userRole === 'admin' && (
                                <button onClick={() => handleDeleteCar(car.id)} className="p-3 bg-white text-gray-300 hover:text-danger hover:border-danger rounded-xl border border-gray-100 transition-all"><Trash2 size={16} /></button>
                              )}
                              {userRole !== 'admin' && userRole !== 'sales_manager' && (
                                <Lock size={12} className="text-gray-300" />
                              )}
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {activeTab === 'requests' && (
            <div className="animate-fade-in">
              <h2 className="text-2xl font-heading font-bold uppercase mb-10">Market Leads</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                {requests.map(req => (
                  <div key={req.id} className="bg-white p-8 rounded-[2rem] border border-gray-100 hover:shadow-2xl transition-all">
                    <div className="flex items-center gap-3 mb-6">
                       <div className="bg-primary/5 p-3 rounded-2xl text-primary"><User size={20} /></div>
                       <div><h3 className="font-heading font-bold text-brandDark text-xl uppercase tracking-tight">{req.name}</h3></div>
                    </div>
                    <div className="space-y-3 mb-8 text-[10px] text-gray-500 uppercase font-bold tracking-widest">
                       <div className="flex items-center gap-3"><Mail size={14}/> {req.email}</div>
                       <div className="flex items-center gap-3"><Phone size={14}/> {req.phone}</div>
                    </div>
                    <div className="bg-brandGray/50 p-6 rounded-2xl border border-gray-50">
                      <p className="text-sm text-brandDark font-bold">{req.yearRange} {req.make} {req.model}</p>
                      <p className="text-[11px] text-primary font-bold">{req.priceRange}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {activeTab === 'testdrives' && (
            <div className="animate-fade-in">
              <h2 className="text-2xl font-heading font-bold uppercase mb-10">Sales Appointments</h2>
              <div className="overflow-hidden rounded-[2rem] border border-gray-100 shadow-sm bg-white">
                 <table className="w-full text-left text-gray-600 border-collapse">
                    <thead className="bg-brandGray/50 text-[9px] uppercase font-bold tracking-[0.2em]">
                      <tr>
                        <th className="px-8 py-6 border-b border-gray-50">Client</th>
                        <th className="px-8 py-6 border-b border-gray-50">Selection</th>
                        <th className="px-8 py-6 border-b border-gray-50">Date</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-50">
                      {testDrives.map(td => (
                         <tr key={td.id} className="hover:bg-brandGray/20 transition-all text-xs font-bold uppercase">
                            <td className="px-8 py-6">{td.customerName}</td>
                            <td className="px-8 py-6 text-primary">{td.carName}</td>
                            <td className="px-8 py-6 text-gray-400">{td.preferredDate} @ {td.preferredTime}</td>
                         </tr>
                      ))}
                    </tbody>
                 </table>
              </div>
            </div>
          )}

          {activeTab === 'staff' && (
            <div className="animate-fade-in grid grid-cols-1 lg:grid-cols-3 gap-10">
              {/* LEFT COLUMN: STAFF PROFILE & PASSWORD CONTROL */}
              <div className="lg:col-span-1 bg-brandGray/20 p-8 rounded-[2rem] border border-gray-100 flex flex-col justify-between h-fit animate-fade-in">
                <div>
                  <h3 className="font-heading font-bold text-lg text-brandDark uppercase tracking-wider mb-2 flex items-center gap-2">
                    <Key size={18} className="text-primary" />
                    Security Keycard
                  </h3>
                  <p className="text-[10px] text-gray-400 font-bold uppercase tracking-widest mb-6">
                    Update your authorization password
                  </p>

                  <form onSubmit={handleChangePassword} className="space-y-6">
                    <div>
                      <label className="block text-[9px] font-bold uppercase tracking-[0.2em] text-gray-400 mb-2">Logged in as</label>
                      <div className="p-4 bg-white rounded-xl border border-gray-100 text-xs text-brandDark font-bold font-mono break-all shadow-sm">
                        {auth.currentUser?.email}
                      </div>
                    </div>

                    <div>
                      <label className="block text-[9px] font-bold uppercase tracking-[0.2em] text-gray-400 mb-2">New Password</label>
                      <input 
                        type="password" 
                        required
                        value={newPasswordValue}
                        onChange={(e) => setNewPasswordValue(e.target.value)}
                        placeholder="••••••••" 
                        className="w-full bg-white border border-gray-100 p-4 rounded-xl text-brandDark text-sm focus:ring-1 focus:ring-primary outline-none shadow-sm font-mono" 
                      />
                    </div>

                    {passwordError && (
                      <div className="text-[10px] text-danger font-bold uppercase tracking-wider bg-red-50 p-3 rounded-lg flex items-center gap-1.5 border border-red-100">
                        <AlertTriangle size={12} /> {passwordError}
                      </div>
                    )}

                    {passwordSuccess && (
                      <div className="text-[10px] text-emerald-800 font-bold uppercase tracking-wider bg-emerald-50 p-3 rounded-lg flex items-center gap-1.5 border border-emerald-100">
                        <Check size={12} /> {passwordSuccess}
                      </div>
                    )}

                    <button 
                      type="submit" 
                      disabled={isChangingPassword}
                      className="w-full bg-brandDark hover:bg-primary text-white py-3.5 rounded-xl font-bold uppercase text-[10px] tracking-widest transition-all shadow-md flex items-center justify-center gap-2 disabled:opacity-40 hover:scale-[1.02] active:scale-100"
                    >
                      {isChangingPassword ? 'Updating...' : 'Update Password'}
                    </button>
                  </form>
                </div>
              </div>

              {/* RIGHT 2 COLUMNS: ADMIN STAFF MANAGEMENT */}
              <div className="lg:col-span-2 space-y-10 animate-fade-in">
                {userRole === 'admin' ? (
                  <>
                    {/* STAFF REGISTER/EDIT PANEL */}
                    <div className="bg-white p-8 rounded-[2.5rem] border border-gray-100 shadow-sm">
                      <div className="flex justify-between items-center mb-8">
                        <div>
                          <h3 className="font-heading font-bold text-xl text-brandDark uppercase tracking-wider flex items-center gap-2">
                            {editingStaffId ? (
                              <>
                                <Edit size={20} className="text-primary" />
                                Edit Staff Member
                              </>
                            ) : (
                              <>
                                <UserPlus size={20} className="text-[#FF9F1C]" />
                                Register Staff Member
                              </>
                            )}
                          </h3>
                          <p className="text-[10px] text-gray-400 font-bold uppercase tracking-widest mt-1">
                            {editingStaffId ? 'Modify staff credentials or access role parameters' : 'Provision a new account with custom role permissions'}
                          </p>
                        </div>
                      </div>

                      <form onSubmit={editingStaffId ? handleSaveStaff : handleCreateStaff} className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div>
                          <label className="block text-[9px] font-bold uppercase tracking-[0.2em] text-gray-400 mb-2">Full Name</label>
                          <input 
                            type="text" 
                            required
                            value={newStaffName}
                            onChange={(e) => setNewStaffName(e.target.value)}
                            placeholder="John Doe" 
                            className="w-full bg-brandGray/30 border border-gray-100 p-4 rounded-xl text-brandDark text-sm focus:ring-1 focus:ring-primary outline-none shadow-sm font-semibold" 
                          />
                        </div>

                        <div>
                          <label className="block text-[9px] font-bold uppercase tracking-[0.2em] text-gray-400 mb-2">Email Address</label>
                          <input 
                            type="email" 
                            required
                            disabled={!!editingStaffId}
                            value={newStaffEmail}
                            onChange={(e) => setNewStaffEmail(e.target.value)}
                            placeholder="john@dakarautosales.com" 
                            className="w-full bg-brandGray/30 border border-gray-100 p-4 rounded-xl text-brandDark text-sm focus:ring-1 focus:ring-primary outline-none shadow-sm font-semibold disabled:opacity-50 disabled:cursor-not-allowed" 
                          />
                        </div>

                        {editingStaffId ? (
                          <div className="flex flex-col justify-end">
                            <label className="block text-[9px] font-bold uppercase tracking-[0.2em] text-gray-400 mb-2">Password Security</label>
                            <button
                              type="button"
                              onClick={handleSendStaffPasswordReset}
                              className="w-full bg-primary/10 hover:bg-primary/20 text-primary border border-primary/20 p-4 rounded-xl font-bold uppercase text-[9px] tracking-wider transition-all flex items-center justify-center gap-2"
                            >
                              <Key size={14} /> Send Password Reset Link
                            </button>
                          </div>
                        ) : (
                          <div>
                            <label className="block text-[9px] font-bold uppercase tracking-[0.2em] text-gray-400 mb-2">Initial Password</label>
                            <input 
                              type="password" 
                              required
                              value={newStaffPassword}
                              onChange={(e) => setNewStaffPassword(e.target.value)}
                              placeholder="••••••••" 
                              className="w-full bg-brandGray/30 border border-gray-100 p-4 rounded-xl text-brandDark text-sm focus:ring-1 focus:ring-primary outline-none shadow-sm font-mono" 
                            />
                          </div>
                        )}

                        <div>
                          <label className="block text-[9px] font-bold uppercase tracking-[0.2em] text-gray-400 mb-2">Access Role Level</label>
                          <select 
                            value={newStaffRole}
                            onChange={(e) => setNewStaffRole(e.target.value as any)}
                            className="w-full bg-brandGray/30 border border-gray-100 p-4 rounded-xl text-brandDark text-sm focus:ring-1 focus:ring-primary outline-none cursor-pointer shadow-sm font-bold text-gray-700"
                          >
                            <option value="sales_manager">Sales Manager (Add/Edit Inventory, NO Delete)</option>
                            <option value="admin">Administrator (Full Access & Staff Management)</option>
                            <option value="staff">Standard Staff (Operational/View Access)</option>
                          </select>
                        </div>

                        <div className="md:col-span-2 flex flex-col gap-4 mt-2">
                          {staffError && (
                            <div className="text-[10px] text-danger font-bold uppercase tracking-wider bg-red-50 p-4 rounded-xl flex items-center gap-2 border border-red-100">
                              <AlertTriangle size={14} className="text-danger" /> {staffError}
                            </div>
                          )}

                          {staffSuccess && (
                            <div className="text-[10px] text-emerald-800 font-bold uppercase tracking-wider bg-emerald-50 p-4 rounded-xl flex items-center gap-2 border border-emerald-100">
                              <Check size={14} className="text-emerald-700" /> {staffSuccess}
                            </div>
                          )}

                          <div className="flex justify-end gap-3 pt-4 border-t border-gray-100">
                            {editingStaffId && (
                              <button 
                                type="button"
                                onClick={handleCancelEditStaff}
                                className="px-8 py-3.5 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-xl font-bold uppercase text-[9px] tracking-widest transition-all"
                              >
                                Cancel
                              </button>
                            )}
                            <button 
                              type="submit" 
                              disabled={isLoadingStaff}
                              className={`px-8 py-3.5 rounded-xl font-bold uppercase text-[9px] tracking-widest flex items-center gap-2 transition-all shadow-md disabled:opacity-40 hover:scale-[1.02] active:scale-100 ${
                                editingStaffId ? 'bg-primary hover:bg-primary/90 text-white' : 'bg-[#FF9F1C] hover:bg-[#ff8f00] text-white'
                              }`}
                            >
                              {isLoadingStaff ? (
                                <div className="h-3 w-3 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                              ) : editingStaffId ? (
                                <Check size={14} />
                              ) : (
                                <UserPlus size={14} />
                              )}
                              {editingStaffId ? 'Save Changes' : 'Register Staff Member'}
                            </button>
                          </div>
                        </div>
                      </form>
                    </div>

                    {/* STAFF DIRECTORY LIST */}
                    <div className="bg-white rounded-[2.5rem] border border-gray-100 p-8 shadow-sm">
                      <div className="mb-6">
                        <h3 className="font-heading font-bold text-xl text-brandDark uppercase tracking-wider flex items-center gap-2">
                          <Users size={20} className="text-primary" />
                          Authorized Dealership Roster
                        </h3>
                        <p className="text-[10px] text-gray-400 font-bold uppercase tracking-widest mt-1">
                          Active staff members authenticated to database services
                        </p>
                      </div>

                      <div className="overflow-x-auto rounded-[2rem] border border-gray-100 shadow-sm bg-white">
                        <table className="w-full text-left text-gray-600 border-collapse">
                          <thead className="bg-brandGray/50 text-[9px] uppercase font-bold tracking-[0.2em]">
                            <tr>
                              <th className="px-6 py-4 border-b border-gray-50">Name / Email</th>
                              <th className="px-6 py-4 border-b border-gray-50">Access Role</th>
                              <th className="px-6 py-4 border-b border-gray-50">Registered At</th>
                              <th className="px-6 py-4 border-b border-gray-50 text-right">Actions</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-50">
                            {staffMembers.length === 0 ? (
                              <tr>
                                <td colSpan={4} className="px-6 py-12 text-center text-xs text-gray-400 uppercase tracking-widest font-bold">
                                  No dealership staff listed. Registered accounts will appear here.
                                </td>
                              </tr>
                            ) : (
                              staffMembers.map(member => (
                                <tr key={member.id} className="hover:bg-brandGray/10 transition-all text-xs font-bold uppercase">
                                  <td className="px-6 py-4">
                                    <div className="flex flex-col">
                                      <span className="text-brandDark font-semibold">{member.name}</span>
                                      <span className="text-[10px] text-gray-400 lowercase font-mono font-medium mt-0.5">{member.email}</span>
                                    </div>
                                  </td>
                                  <td className="px-6 py-4">
                                    <span className={`px-2.5 py-1 rounded text-[8px] font-bold uppercase tracking-wider border inline-block ${
                                      member.role === 'admin' ? 'bg-red-50 text-red-700 border-red-200' : 
                                      member.role === 'sales_manager' ? 'bg-emerald-50 text-emerald-800 border-emerald-200' : 'bg-gray-50 text-gray-650 border-gray-200'
                                    }`}>
                                      {member.role === 'admin' ? 'Dealership Owner' : 
                                       member.role === 'sales_manager' ? 'Sales Manager' : 'Operational Staff'}
                                    </span>
                                  </td>
                                  <td className="px-6 py-4 text-gray-400 font-medium">
                                    {member.createdAt ? new Date(member.createdAt).toLocaleDateString() : 'N/A'}
                                  </td>
                                  <td className="px-6 py-4 text-right">
                                    <div className="flex justify-end items-center gap-2">
                                      <button 
                                        type="button"
                                        onClick={() => handleStartEditStaff(member)}
                                        className="p-2.5 text-gray-400 hover:text-primary hover:border-primary rounded-xl border border-gray-100 hover:bg-blue-50/50 transition-all"
                                        title="Edit Staff Member"
                                      >
                                        <Edit size={14} />
                                      </button>
                                      {member.email === 'nassoroa@gmail.com' || member.email === auth.currentUser?.email ? (
                                        <span className="text-[9px] text-gray-400 font-bold uppercase tracking-widest px-3 py-2 bg-gray-50 border border-gray-100 rounded-xl">Protected</span>
                                      ) : (
                                        <button 
                                          type="button"
                                          onClick={() => handleDeleteStaff(member.id, member.name)}
                                          className="p-2.5 text-gray-400 hover:text-danger hover:border-danger rounded-xl border border-gray-100 hover:bg-red-50 transition-all"
                                          title="Delete Staff Member"
                                        >
                                          <Trash2 size={14} />
                                        </button>
                                      )}
                                    </div>
                                  </td>
                                </tr>
                              ))
                            )}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </>
                ) : (
                  <div className="bg-amber-50 border border-amber-200 p-8 rounded-[2rem] text-amber-900 flex items-start gap-4 shadow-sm w-full">
                    <ShieldCheck size={28} className="text-amber-700 flex-shrink-0" />
                    <div>
                      <h4 className="font-heading font-bold text-base uppercase tracking-wider mb-2">Dealership Directory Restricted</h4>
                      <p className="text-xs text-amber-800 leading-relaxed">
                        To register additional operators, adjust active permissions, or audit security rosters, please contact the dealership owner for administrator clearance.
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
